import type { ProposalDetails } from '@/services/quotes/types';
import { cleanCnpjOrCpf } from '@/shared/utils/document.util';
import { useEffect, useRef, useState } from 'react';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useProposalDetails } from '@/features/quotes/hooks/homeowner/use-proposal-details.hook';
import {
  isMorteCoverageId,
  type CapitalGlobalAssistanceCatalogItem,
  type CapitalGlobalCoverageCatalogItem,
} from '@/features/quotes/schemas/capital-global/coverages-catalog';
import { getInitialCoverageValues } from '@/features/quotes/schemas/capital-global/coverage-limits.util';
import { formatCurrency } from '@/shared/utils/currencyFormatter';
import type { CoverageUmbrella } from '@/services/coverages/types';
import type {
  CoveragesDataMap,
  CoveragesEntityValues,
  EntityTab,
} from '../../types';
import {
  DEFAULT_COVERAGES_ENTITY_VALUES,
  getReferenceCapitalForEntity,
  mapCoveragesDataMapFromApi,
  mapProposalDetailsToRiskEntities,
  riskEntitiesFingerprint,
  riskProfileCapitalsFingerprint,
} from '../utils/map-api.util';
import { propagateMorteCapitalChange } from '../utils/propagate-morte-capital.util';
import {
  clearPendingStep3Resync,
  markRiskCapitalsFingerprintSeen,
  needsResyncFromStep2Capital,
} from '../utils/step3-resync.util';

// ─── field names ─────────────────────────────────────────────────────────────

const COVERAGES_FIELD_NAMES: Array<keyof CoveragesEntityValues> = [
  'coverages',
  'assistances',
  'capitalization',
  'assistanceFuneral',
];

// ─── local helpers ───────────────────────────────────────────────────────────

function mergeCoverageState(
  catalog: CapitalGlobalCoverageCatalogItem[],
  prev: CoveragesEntityValues['coverages'],
  morteId: string | undefined
): CoveragesEntityValues['coverages'] {
  const next: CoveragesEntityValues['coverages'] = {};
  for (const coverage of catalog) {
    const existing = prev[coverage.id];
    const isMorte = isMorteCoverageId(coverage.id, morteId);
    next[coverage.id] = existing
      ? { ...existing, enabled: isMorte ? true : existing.enabled }
      : {
          enabled: isMorte,
          maxIndividualCapital: '',
          percent: '',
          benefitCode: '',
        };
  }
  return next;
}

function mergeAssistanceState(
  catalog: CapitalGlobalAssistanceCatalogItem[],
  prev: Record<string, { enabled: boolean }>
): Record<string, { enabled: boolean }> {
  const next: Record<string, { enabled: boolean }> = {};
  for (const assistance of catalog) {
    next[assistance.code] = prev[assistance.code] ?? { enabled: false };
  }
  return next;
}

/**
 * Given a partial entity state (may come from API or be empty), merges the
 * full coverage catalog and assistance catalog so every item is present, and
 * initialises "Morte" with the entity's reference capital when not already set.
 */
function buildFullEntityState(
  partial: CoveragesEntityValues,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  assistanceCatalog: CapitalGlobalAssistanceCatalogItem[],
  morteId: string | undefined,
  referenceCapital: number,
  resyncFromStep2: boolean
): CoveragesEntityValues {
  let mergedCoverages = mergeCoverageState(
    coverageCatalog,
    partial.coverages,
    morteId
  );

  const shouldSyncMorte =
    morteId &&
    referenceCapital > 0 &&
    mergedCoverages[morteId] &&
    (!mergedCoverages[morteId].maxIndividualCapital || resyncFromStep2);

  if (shouldSyncMorte) {
    const morteItem = coverageCatalog.find(c => c.id === morteId);
    if (morteItem) {
      mergedCoverages[morteId] = {
        enabled: true,
        ...getInitialCoverageValues(
          morteItem,
          {
            referenceCapital,
            morteCapital: referenceCapital,
            morteId,
          },
          formatCurrency
        ),
      };

      mergedCoverages = propagateMorteCapitalChange(
        mergedCoverages,
        morteId,
        referenceCapital,
        coverageCatalog,
        referenceCapital
      );
    }
  }

  return {
    coverages: mergedCoverages,
    assistances: mergeAssistanceState(assistanceCatalog, partial.assistances),
    capitalization: partial.capitalization,
    assistanceFuneral: partial.assistanceFuneral,
  };
}

function hasMorteCapital(
  values: CoveragesEntityValues,
  morteId: string | undefined
): boolean {
  return Boolean(morteId && values.coverages[morteId]?.maxIndividualCapital);
}

function hydrateCoveragesMapFromApi(
  proposalDetails: ProposalDetails,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  morteId: string,
  assistanceUmbrella: CoverageUmbrella | null,
  capitalizationUmbrella: CoverageUmbrella | null,
  resyncFromStep2: boolean
): CoveragesDataMap {
  const hasSavedCoverages = proposalDetails.coverageProfileList?.some(entry => {
    if (entry.groups?.some(group => group.coverages.length > 0)) {
      return true;
    }
    return (entry.coverages?.length ?? 0) > 0;
  });

  if (!hasSavedCoverages) return {};

  return mapCoveragesDataMapFromApi(
    proposalDetails,
    coverageCatalog,
    morteId,
    assistanceUmbrella,
    capitalizationUmbrella,
    { resyncFromStep2 }
  );
}

function buildAllEntityStates(
  entities: EntityTab[],
  coveragesDataMap: CoveragesDataMap,
  proposalDetails: ProposalDetails,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  assistanceCatalog: CapitalGlobalAssistanceCatalogItem[],
  morteId: string,
  resyncFromStep2: boolean
): CoveragesDataMap {
  const nextMap: CoveragesDataMap = { ...coveragesDataMap };

  for (const entity of entities) {
    const key = cleanCnpjOrCpf(entity.id) || entity.id;
    const partial = nextMap[key] ?? DEFAULT_COVERAGES_ENTITY_VALUES;
    const refCap = getReferenceCapitalForEntity(proposalDetails, entity.id);
    nextMap[key] = buildFullEntityState(
      partial,
      coverageCatalog,
      assistanceCatalog,
      morteId,
      refCap,
      resyncFromStep2
    );
  }

  return nextMap;
}

// ─── types ───────────────────────────────────────────────────────────────────

type CoveragesEntityFormApi = {
  getFieldValue: (name: string) => unknown;
  setFieldValue: (name: string, value: unknown) => void;
};

type Params = {
  form: unknown;
  coverageCatalog: CapitalGlobalCoverageCatalogItem[];
  assistanceCatalog: CapitalGlobalAssistanceCatalogItem[];
  assistanceUmbrella: CoverageUmbrella | null;
  capitalizationUmbrella: CoverageUmbrella | null;
  morteId: string | undefined;
  isCatalogLoading: boolean;
  isCatalogError: boolean;
};

type Result = {
  entities: EntityTab[];
  hasMultipleEntities: boolean;
  activeEntityId: string;
  referenceCapital: number;
  handleTabChange: (newEntityId: string) => void;
  captureAllEntities: () => CoveragesDataMap;
  applyEntityValues: (values: CoveragesDataMap) => void;
};

// ─── hook ────────────────────────────────────────────────────────────────────

export function useStepCoveragesEntityTabs({
  form: formRaw,
  coverageCatalog,
  assistanceCatalog,
  assistanceUmbrella,
  capitalizationUmbrella,
  morteId,
  isCatalogLoading,
  isCatalogError,
}: Params): Result {
  const form = formRaw as CoveragesEntityFormApi;
  const { policyId } = useQuoteParams();
  const resolvedPolicyId = (policyId || '').trim();
  const { data: proposalDetails } = useProposalDetails(
    resolvedPolicyId || undefined
  );

  const entities = mapProposalDetailsToRiskEntities(proposalDetails);
  const hasMultipleEntities = entities.length > 1;
  const entitiesFingerprint = riskEntitiesFingerprint(proposalDetails);

  const [activeEntityId, setActiveEntityId] = useState<string>('');

  const coveragesDataMapRef = useRef<CoveragesDataMap>({});
  const initializedEntitiesRef = useRef<Set<string>>(new Set());
  const apiHydratedRef = useRef(false);
  const syncedEntitiesFingerprintRef = useRef('');
  const resyncCompletedForFingerprintRef = useRef('');
  const lastRestoredActiveEntityRef = useRef('');

  function normalizeDoc(id: string): string {
    // id da entidade é o documento do estipulante — CNPJ pode ser alfanumérico.
    return cleanCnpjOrCpf(id) || id;
  }

  function captureCurrentValues(): CoveragesEntityValues {
    return COVERAGES_FIELD_NAMES.reduce<CoveragesEntityValues>(
      (acc, key) => {
        (acc as Record<string, unknown>)[key] = form.getFieldValue(key);
        return acc;
      },
      { ...DEFAULT_COVERAGES_ENTITY_VALUES }
    );
  }

  function restoreValues(values: CoveragesEntityValues) {
    COVERAGES_FIELD_NAMES.forEach(key => {
      form.setFieldValue(key, values[key]);
    });
  }

  useEffect(() => {
    if (entitiesFingerprint === syncedEntitiesFingerprintRef.current) {
      return;
    }
    syncedEntitiesFingerprintRef.current = entitiesFingerprint;

    const allowedKeys = new Set(
      entities.map(entity => normalizeDoc(entity.id))
    );
    coveragesDataMapRef.current = Object.fromEntries(
      Object.entries(coveragesDataMapRef.current).filter(([key]) =>
        allowedKeys.has(key)
      )
    );

    for (const key of [...initializedEntitiesRef.current]) {
      if (!allowedKeys.has(key)) {
        initializedEntitiesRef.current.delete(key);
      }
    }

    apiHydratedRef.current = false;
    resyncCompletedForFingerprintRef.current = '';
    lastRestoredActiveEntityRef.current = '';

    if (entities.length === 0) {
      if (activeEntityId) {
        setActiveEntityId('');
      }
      return;
    }

    const entityIdSet = new Set(entities.map(entity => entity.id));
    if (!entityIdSet.has(activeEntityId)) {
      setActiveEntityId(entities[0].id);
    }
  }, [entities, entitiesFingerprint, activeEntityId]);

  useEffect(() => {
    if (isCatalogLoading || isCatalogError) return;
    if (!coverageCatalog.length || !assistanceCatalog.length) return;
    if (!morteId) return;
    if (!proposalDetails) return;

    if (!activeEntityId) {
      if (entities.length > 0) {
        setActiveEntityId(entities[0].id);
      }
      return;
    }

    const key = normalizeDoc(activeEntityId);
    const hasRiskProfiles = Boolean(proposalDetails.riskProfileList?.length);
    if (!hasRiskProfiles) return;

    const currentFingerprint = riskProfileCapitalsFingerprint(proposalDetails);
    const resyncFromStep2 = needsResyncFromStep2Capital(
      resolvedPolicyId,
      proposalDetails
    );
    const shouldRunFullResync =
      resyncFromStep2 &&
      currentFingerprint &&
      resyncCompletedForFingerprintRef.current !== currentFingerprint;

    if (shouldRunFullResync) {
      coveragesDataMapRef.current = {};
      initializedEntitiesRef.current.clear();
      apiHydratedRef.current = false;
    }

    if (!apiHydratedRef.current) {
      const apiMap = hydrateCoveragesMapFromApi(
        proposalDetails,
        coverageCatalog,
        morteId,
        assistanceUmbrella,
        capitalizationUmbrella,
        resyncFromStep2
      );

      if (shouldRunFullResync) {
        coveragesDataMapRef.current = apiMap;
      } else {
        for (const [apiKey, apiVal] of Object.entries(apiMap)) {
          if (!coveragesDataMapRef.current[apiKey]) {
            coveragesDataMapRef.current[apiKey] = apiVal;
          }
        }
      }

      apiHydratedRef.current = true;
    }

    if (shouldRunFullResync) {
      coveragesDataMapRef.current = buildAllEntityStates(
        entities,
        coveragesDataMapRef.current,
        proposalDetails,
        coverageCatalog,
        assistanceCatalog,
        morteId,
        true
      );

      for (const entity of entities) {
        const entityKey = normalizeDoc(entity.id);
        if (hasMorteCapital(coveragesDataMapRef.current[entityKey], morteId)) {
          initializedEntitiesRef.current.add(entityKey);
        }
      }

      restoreValues(
        coveragesDataMapRef.current[key] ?? DEFAULT_COVERAGES_ENTITY_VALUES
      );
      lastRestoredActiveEntityRef.current = activeEntityId;
      markRiskCapitalsFingerprintSeen(resolvedPolicyId, proposalDetails);
      clearPendingStep3Resync(resolvedPolicyId);
      resyncCompletedForFingerprintRef.current = currentFingerprint;
      return;
    }

    if (initializedEntitiesRef.current.has(key)) {
      // Evita sobrescrever edições em andamento quando deps do effect
      // (ex.: catálogo) recebem nova referência de objeto.
      if (lastRestoredActiveEntityRef.current !== activeEntityId) {
        restoreValues(
          coveragesDataMapRef.current[key] ?? DEFAULT_COVERAGES_ENTITY_VALUES
        );
        lastRestoredActiveEntityRef.current = activeEntityId;
      }
      return;
    }

    const partial =
      coveragesDataMapRef.current[key] ?? DEFAULT_COVERAGES_ENTITY_VALUES;
    const refCap = getReferenceCapitalForEntity(
      proposalDetails,
      activeEntityId
    );

    const fullState = buildFullEntityState(
      partial,
      coverageCatalog,
      assistanceCatalog,
      morteId,
      refCap,
      false
    );

    coveragesDataMapRef.current[key] = fullState;
    if (hasMorteCapital(fullState, morteId)) {
      initializedEntitiesRef.current.add(key);
    }
    restoreValues(fullState);
    lastRestoredActiveEntityRef.current = activeEntityId;

    if (!resyncFromStep2) {
      markRiskCapitalsFingerprintSeen(resolvedPolicyId, proposalDetails);
      clearPendingStep3Resync(resolvedPolicyId);
      if (currentFingerprint) {
        resyncCompletedForFingerprintRef.current = currentFingerprint;
      }
    }
  }, [
    isCatalogLoading,
    isCatalogError,
    coverageCatalog,
    assistanceCatalog,
    morteId,
    activeEntityId,
    entities,
    proposalDetails,
    assistanceUmbrella,
    capitalizationUmbrella,
    resolvedPolicyId,
  ]);

  function handleTabChange(newEntityId: string) {
    coveragesDataMapRef.current[normalizeDoc(activeEntityId)] =
      captureCurrentValues();

    const key = normalizeDoc(newEntityId);
    const savedState = coveragesDataMapRef.current[key];

    if (
      initializedEntitiesRef.current.has(key) &&
      savedState &&
      !isCatalogLoading &&
      !isCatalogError &&
      coverageCatalog.length &&
      morteId &&
      proposalDetails
    ) {
      restoreValues(savedState);
      lastRestoredActiveEntityRef.current = newEntityId;
      setActiveEntityId(newEntityId);
      return;
    }

    if (
      !isCatalogLoading &&
      !isCatalogError &&
      coverageCatalog.length &&
      morteId &&
      proposalDetails
    ) {
      const refCap = getReferenceCapitalForEntity(proposalDetails, newEntityId);
      const resyncFromStep2 = needsResyncFromStep2Capital(
        resolvedPolicyId,
        proposalDetails
      );
      const fullState = buildFullEntityState(
        savedState ?? DEFAULT_COVERAGES_ENTITY_VALUES,
        coverageCatalog,
        assistanceCatalog,
        morteId,
        refCap,
        resyncFromStep2
      );
      coveragesDataMapRef.current[key] = fullState;
      if (hasMorteCapital(fullState, morteId)) {
        initializedEntitiesRef.current.add(key);
      }
      restoreValues(fullState);
    } else {
      restoreValues(savedState ?? DEFAULT_COVERAGES_ENTITY_VALUES);
    }

    lastRestoredActiveEntityRef.current = newEntityId;
    setActiveEntityId(newEntityId);
  }

  function captureAllEntities(): CoveragesDataMap {
    return {
      ...coveragesDataMapRef.current,
      [normalizeDoc(activeEntityId)]: captureCurrentValues(),
    };
  }

  /**
   * Grava valores já confirmados (percentuais commitados na validação) de volta
   * no estado por entidade e reflete a entidade ativa no formulário, para a tela
   * mostrar exatamente o que foi validado.
   */
  function applyEntityValues(values: CoveragesDataMap) {
    coveragesDataMapRef.current = {
      ...coveragesDataMapRef.current,
      ...values,
    };

    const activeValues =
      coveragesDataMapRef.current[normalizeDoc(activeEntityId)];
    if (activeValues) {
      restoreValues(activeValues);
      lastRestoredActiveEntityRef.current = activeEntityId;
    }
  }

  const referenceCapital = getReferenceCapitalForEntity(
    proposalDetails,
    activeEntityId
  );

  return {
    entities,
    hasMultipleEntities,
    activeEntityId,
    referenceCapital,
    handleTabChange,
    captureAllEntities,
    applyEntityValues,
  };
}
